import addVoucher from "@/actions/selection/add-voucher"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import TextField from "@/app/(site)/_components/Molecules/TextField"
import { getLangCode } from "@/app/(site)/_utils/misc"
import { t } from "@/translations"
import { Err, Ok } from "@/types/generic/Result"
import { useQueryClient } from "@tanstack/react-query"
import { usePathname } from "next/navigation"
import { useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { match } from "ts-pattern"

interface FormValues {
    promotionCode: string
}

export default function PromotionCode() {
    const [showPromotionCode, setShowPromotionCode] = useState(false)
    const path = usePathname()
    const langCode = getLangCode(path.split("/")[1]) ?? ""
    const queryClient = useQueryClient()
    const {
        handleSubmit,
        control,
        setError,
        clearErrors,
        reset,
        formState: { errors }
    } = useForm<FormValues>({
        mode: "onChange"
    })

    const onSubmit = async (data: FormValues) => {
        const previousCart: any = queryClient.getQueryData(["selection"])
        const vouchers =
            previousCart?.data?.checkoutApiSelection?.selection?.discounts?.vouchers

        const voucherExists = vouchers?.some(
            (voucher: any) => voucher.voucher === data.promotionCode
        )

        if (voucherExists) {
            setError("promotionCode", {
                type: "manual",
                message: "Voucher already added"
            })
            return // Stop the execution if the voucher already exists
        }

        try {
            const res = await addVoucher({ voucher: data.promotionCode })
            match(res)
                .with(Ok, () => {
                    clearErrors("promotionCode")
                    queryClient.invalidateQueries({ queryKey: ["selection"] })
                    reset({ promotionCode: "" })
                })
                .with(Err, () => {
                    setError("promotionCode", {
                        type: "manual",
                        message: t(langCode, "checkout.invalid_voucher_code")
                    })
                })
                .exhaustive()
        } catch (error) {
            console.error(error)
            setError("promotionCode", {
                type: "manual",
                message: t(
                    langCode,
                    "checkout.information_for_billing_and_shipping.invalid"
                )
            })
        }
    }

    return (
        <div>
            {showPromotionCode ? (
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Text
                        size="small"
                        weight="regular"
                        className={`mb-8 ${errors.promotionCode ? "text-red-500" : ""}`}
                    >
                        {t(langCode, "checkout.voucher_code")}
                        {errors.promotionCode && `: ${errors.promotionCode.message}`}
                    </Text>
                    <Controller
                        name="promotionCode"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <TextField
                                theme="light"
                                id="promotionCode"
                                type="text"
                                aria-label="Enter Voucher Code"
                                placeholder={t(langCode, "checkout.voucher_enter")}
                                className={`mb-12 w-full ${error ? "border-red-500" : ""}`}
                                onInput={() => clearErrors("promotionCode")}
                                {...field}
                            />
                        )}
                    />
                    <ButtonPill
                        type="submit"
                        variant="primary"
                        className="w-full"
                        color="black"
                        size="large"
                        aria-label="Apply Voucher Code"
                        disabled={Object.keys(errors).length > 0}
                    >
                        {t(langCode, "common.apply")}
                    </ButtonPill>
                </form>
            ) : (
                <ButtonPill
                    variant="primary"
                    className="w-full"
                    size="large"
                    color="black"
                    aria-label="Add Voucher Code"
                    onClick={() => setShowPromotionCode(true)}
                >
                    {t(langCode, "checkout.voucher_add")}
                </ButtonPill>
            )}
        </div>
    )
}
